import ArrowDownButtonEndIcon from '@assets/jsx-icon/ArrowDownButtonEndIcon';
import { Box, Button, ClickAwayListener, Fade, MenuItem, Popper, Stack, TableCell, Typography } from '@mui/material';
import color from '@theme/Colors';
import React, { useState } from 'react';
import { DropdownType, DropdownTypeStyles, mappingPriorityTask, mappingStatusTask, stylesMapping } from '../config';
import { CameraTask, TaskPriority, TaskStatus } from '@EcamModel/model/CameraTask';

interface StatusPriorityDropdownProps {
    type: DropdownType;
    menuItems: (TaskStatus | TaskPriority | null)[];
    label?: TaskStatus | TaskPriority | null;
    task: CameraTask;
    handleChangeStatusOrPriority: (
        selectedItem: TaskStatus | TaskPriority | null,
        taskId: number,
        type: DropdownType
    ) => void;
}

export default function StatusPriorityDropdown(props: StatusPriorityDropdownProps) {
    const { type, label, menuItems, task, handleChangeStatusOrPriority } = props;
    const anchorRef = React.useRef<HTMLButtonElement>(null);

    const [menuAnchorElement, setMenuAnchorElement] = useState<boolean>(false);

    const buttonStyles = stylesMapping[type][label as keyof DropdownTypeStyles] || {};
    const isStatusComplete = task.TaskStatus === TaskStatus.Completed;

    const handleMenuButtonClick = () => {
        setMenuAnchorElement((prevOpen) => !prevOpen);
    };

    const handleClose = (event: Event | React.SyntheticEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        setMenuAnchorElement(false);
    };

    return (
        <TableCell align="center">
            <Button
                ref={anchorRef}
                disableRipple={isStatusComplete}
                onClick={isStatusComplete ? undefined : handleMenuButtonClick}
                endIcon={
                    !isStatusComplete ? (
                        <ArrowDownButtonEndIcon
                            style={{
                                marginBottom: '2px',
                                transform: menuAnchorElement ? 'rotate(-180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s ease',
                            }}
                            stroke={buttonStyles.color}
                        />
                    ) : null
                }
                sx={{
                    fontSize: 12,
                    padding: '4px 10px',
                    width: 'max-content',
                    backgroundColor: buttonStyles.backgroundColor,
                    color: buttonStyles.color,
                    cursor: isStatusComplete ? 'default' : 'pointer',
                    '&:hover': {
                        backgroundColor: buttonStyles.hoverColor || buttonStyles.backgroundColor,
                    },
                }}
            >
                {type === DropdownType.Status
                    ? mappingStatusTask?.[label as TaskStatus]?.toUpperCase()
                    : mappingPriorityTask?.[label as TaskPriority]?.toUpperCase()}
            </Button>
            <ClickAwayListener onClickAway={handleClose}>
                <Popper
                    anchorEl={anchorRef.current}
                    open={menuAnchorElement}
                    sx={{
                        backgroundColor: color.white,
                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                        p: '4px 0px',
                        transition: 'opacity 0.3s ease',
                        zIndex: 10000,
                    }}
                    placement="bottom-start"
                    transition
                >
                    {({ TransitionProps }) => (
                        <Fade {...TransitionProps} timeout={200}>
                            <Box>
                                {menuItems
                                    .filter((item) => item !== label)
                                    .map((item, index) => {
                                        return (
                                            <MenuItem
                                                key={index}
                                                onClick={() => {
                                                    handleChangeStatusOrPriority(
                                                        type === DropdownType.Status
                                                            ? (item as TaskStatus)
                                                            : (item as TaskPriority),
                                                        task.Id!,
                                                        type
                                                    );
                                                    setMenuAnchorElement(false);
                                                }}
                                                sx={{
                                                    fontSize: 12,
                                                    width: type === DropdownType.Status ? '150px' : '100px',
                                                    backgroundColor: 'none',
                                                    transition: 'background-color 0.2s ease',
                                                }}
                                            >
                                                {type === DropdownType.Status
                                                    ? mappingStatusTask?.[item as TaskStatus]?.toUpperCase()
                                                    : mappingPriorityTask?.[item as TaskPriority]?.toUpperCase()}
                                            </MenuItem>
                                        );
                                    })}
                            </Box>
                        </Fade>
                    )}
                </Popper>
            </ClickAwayListener>
        </TableCell>
    );
}
